import { useCallback, useMemo, useState } from 'react';
import { Button, ButtonSizes, ButtonVariants, Dropdown, Table } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { buildTableLabels } from '@/config/tableLabels';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetSiteMbPeriodQuery } from '../../api/sitesApi';
import { buildSiteMbPeriodColumns } from '../../constants/siteMbPeriodColumns';
import { MassBalancePeriodModal } from '../MassBalancePeriod/MassBalancePeriodModal';
import styles from './SiteMassBalancePeriodTabView.module.scss';

const getMbPeriodRowId = (row) => row.id;

/**
 * Props:
 *   site  the site detail view model — its terminals scope this tab
 */
export const SiteMassBalancePeriodTabView = ({ site }) => {
  const t = useTranslations();
  const [terminalId, setTerminalId] = useState(() => String(site.terminals[0]?.id ?? ''));
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isFetching, isError, refetch } = useGetSiteMbPeriodQuery({
    siteId: site.id,
    terminalId,
  });

  const rows = data?.rows ?? EMPTY_LIST;
  const hasPeriods = rows.length > 0;
  const hasTerminals = site.terminals.length > 0;

  const handleTerminalChange = useCallback((event) => setTerminalId(event.target.value), []);
  const handleRetry = useCallback(() => refetch(), [refetch]);
  const handleEditMbPeriod = useCallback(() => setIsModalOpen(true), []);
  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  const columns = useMemo(() => buildSiteMbPeriodColumns(t), [t]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const terminalOptions = useMemo(
    () =>
      site.terminals.map((terminal) => ({
        value: String(terminal.id),
        label: terminal.terminalName,
      })),
    [site.terminals],
  );

  const emptyState = useMemo(() => {
    if (isError) {
      return {
        title: t('SITES_MB_PERIOD_LOAD_ERROR'),
        primaryLabel: t('COMMON_RETRY'),
        onPrimaryClick: handleRetry,
      };
    }

    return {
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('SITES_MB_PERIOD_EMPTY_TITLE'),
      description: t('SITES_MB_PERIOD_EMPTY_DESCRIPTION'),
      primaryLabel: t('SITES_MB_PERIOD_ACTION_UPDATE'),
      onPrimaryClick: handleEditMbPeriod,
      primaryIcon: <Plus />,
    };
  }, [t, isError, handleRetry, handleEditMbPeriod]);

  return (
    <div className={styles.card}>
      {(hasPeriods || hasTerminals) && (
        <div className={styles.toolbar}>
          <div className={styles.scope}>
            {hasTerminals && (
              <Dropdown
                className={styles.terminalSelect}
                value={terminalId}
                onChange={handleTerminalChange}
                options={terminalOptions}
                placeholder={t('FORM_SELECT_TERMINAL')}
                emptyMessage={t('FORM_TERMINAL_EMPTY')}
                aria-label={t('FIELD_TERMINAL_NAME')}
              />
            )}
          </div>

          {hasPeriods && (
            <Button
              variant={ButtonVariants.secondary}
              size={ButtonSizes.sm}
              onClick={handleEditMbPeriod}
            >
              {t('SITES_MB_PERIOD_ACTION_EDIT')}
            </Button>
          )}
        </div>
      )}

      <Table
        columns={columns}
        data={rows}
        getRowId={getMbPeriodRowId}
        loading={isFetching}
        emptyState={emptyState}
        labels={tableLabels}
      />

      <MassBalancePeriodModal
        open={isModalOpen}
        onClose={handleCloseModal}
        site={site}
        terminalId={terminalId}
        mbPeriod={data}
      />
    </div>
  );
};
